import { Badge } from "../common/Badge";
import { daysAgo, formatDateOnly } from "../../services/hiveActions";

/**
 * How a hive's urgency reads, in ONE place (#206).
 *
 * The dashboard's "Your Hives" card already sorted worst-first, tinted its
 * rows and said "18 days ago". The hive list at /hives -- the page somebody
 * with forty hives actually browses -- showed a bare date and no marking at
 * all, so the work of noticing which hive needs you was left to the reader.
 * That was the original complaint, fixed on one screen and not the other.
 *
 * Extracted rather than copied. This codebase's recurring lesson is that a
 * rule written down twice drifts: the gates on routes and in the sync
 * table, the hive cap in six places, the tier names in Python and in a
 * regex. Two lists disagreeing about which hives are overdue would be the
 * same bug in a new place.
 *
 * needs_attention and next_check_due are both computed by the API from the
 * account's inspection interval (V034), so nothing here decides WHEN a hive
 * is due -- only how that reads.
 */

/** Is this hive shut for the season, TODAY (#389)?
 *
 * The same test the SQL makes -- `season_closed_until >= today` -- because
 * a past date is an expired closure, not a closed hive. Reading the column
 * as a boolean would mark a hive closed forever after one winter, and it
 * would do so only on the screen: the server would have reopened it, so
 * the list and the Next Check date beside it would disagree.
 *
 * LOCAL midnight against a plain date. The server compares in UTC, so for
 * a few hours around the boundary the two can differ by a day -- which
 * cannot change whether a hive is due on a 30-day cadence, and matters far
 * less than the alternative: `new Date("2027-03-01")` parses as UTC
 * midnight and renders as 28 February west of Greenwich, so a hive would
 * read as opening a day before it does.
 */
export function isClosedForSeason(hive) {
  if (!hive?.season_closed_until) return false;
  const [y, m, d] = String(hive.season_closed_until).slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return false;
  const today = new Date();
  return new Date(y, m - 1, d) >= new Date(today.getFullYear(), today.getMonth(), today.getDate());
}

/** Never checked outranks overdue.
 *
 * An overdue hive has a history somebody can look at; a never-checked one
 * is an unknown, and unknowns are worse. */
/**
 * Is this hive's season disputed? (#400)
 *
 * `season_conflict_at` is the flag, and the other two columns are what
 * the other person said. The DATE among them is legitimately null --
 * meaning they wanted it open -- so it cannot double as "no dispute".
 *
 * Mirrors mobile's seasonDisputed in services/hivesApi.js. Two clients
 * disagreeing about whether there IS a disagreement would be a joke at
 * the user's expense.
 */
export function seasonDisputed(hive) {
  return Boolean(hive?.season_conflict_at);
}

export function urgencyRank(hive) {
  if (!hive.last_checked) return 2;
  return hive.needs_attention ? 1 : 0;
}

export function byUrgency(hives) {
  return [...(hives || [])].sort((a, b) => urgencyRank(b) - urgencyRank(a));
}

/** The row tint: the glance. The badges below are the answer. */
export function urgencyTone(hive) {
  if (!hive.last_checked) return "danger";
  return hive.needs_attention ? "warning" : null;
}

/** "18 days ago", with the date underneath.
 *
 * Not the date alone: the arithmetic between today and a printed date is
 * exactly the work the reader was being left to do. */
export function lastCheckedCell(hive) {
  if (!hive.last_checked) return "Never";
  return (
    <span style={{ display: "grid" }}>
      <span>{daysAgo(hive.last_checked)}</span>
      <span style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
        {new Date(hive.last_checked).toLocaleDateString()}
      </span>
    </span>
  );
}

/** Words for the state, date for the detail.
 *
 * formatDateOnly, not new Date(...): a bare YYYY-MM-DD parses as UTC
 * midnight and renders a day early in Central time (fixed in 9e64d40). */
export function nextCheckCell(hive) {
  if (!hive.last_checked) return <Badge tone="danger">Never checked</Badge>;
  const label = formatDateOnly(hive.next_check_due);
  const closed = isClosedForSeason(hive);

  /*
   * BOTH badges when both are true, and that is the whole decision here.
   *
   * A closed hive can still be overdue -- the closed cadence is longer,
   * not absent, so forty days since a check against a thirty-day winter
   * interval is genuinely late. Showing only "Closed" would hide work
   * that needs doing; showing only "Overdue" would hide WHY the gap was
   * allowed to get that long, and invite somebody to go and open a hive
   * in January.
   *
   * Closed sits second: overdue is the thing to act on, and the eye
   * should reach it first.
   */
  if (!hive.needs_attention && !closed) return label;
  return (
    <span style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
      {hive.needs_attention && <Badge tone="warning">Overdue</Badge>}
      {closed && (
        <Badge tone="info">Closed until {formatDateOnly(hive.season_closed_until)}</Badge>
      )}
      <span style={{ color: "var(--color-text-muted)" }}>{label}</span>
    </span>
  );
}
